import { motion } from "framer-motion";
import { ArrowUpRight, HeartPulse, Building2, Eye, Smile, Ear, GraduationCap, Sunrise } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { openQuote } from "../lib/site";
import { LifeCard } from "./LifeCard";

const Card = ({ className = "", children, testid, delay = 0 }) => (
  <motion.article
    data-testid={testid}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -6 }}
    className={`group relative overflow-hidden rounded-[28px] ${className}`}
  >
    {children}
  </motion.article>
);

const Quote = ({ id, light }) => (
  <button onClick={() => openQuote("health")} data-testid={`${id}-quote-btn`} className={`mt-6 inline-flex items-center gap-2 text-sm font-semibold ${light ? "text-[#E5A855]" : "text-[#0B281E]"}`}>
    Get a quote <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-500" />
  </button>
);

const PhotoCard = ({ img, alt, children }) => (
  <>
    <img src={img} alt={alt} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s]" />
    <div className="absolute inset-0 bg-[#071510]/60" />
    <div className="relative h-full p-7 lg:p-9 flex flex-col justify-end text-[#FAF8F5]">{children}</div>
  </>
);

export const Services = () => (
  <section id="coverage" data-testid="services-section" className="bg-[#FAF8F5] py-24 lg:py-36">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <Reveal className="lg:col-span-7">
          <Eyebrow>01 — What I cover</Eyebrow>
          <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0B281E] mt-5 leading-[1.02]">Six ways to protect <em className="text-[#A8701F]">everything</em> you've built.</h2>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.1}>
          <p className="text-base md:text-lg text-[#52635B] leading-relaxed">From your first marketplace plan to the policy that pays for your grandkids' tuition, one agent compares the options side by side so you never have to decode the fine print alone.</p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[290px] gap-5 lg:gap-6">
        <Card testid="bento-aca" className="md:col-span-2 lg:col-span-7 lg:row-span-2 min-h-[460px]">
          <PhotoCard img="/img/aca.jpg" alt="Family smiling together outdoors">
            <HeartPulse className="w-8 h-8 text-[#E5A855] mb-auto" />
            <p className="font-mono-d text-xs uppercase tracking-[0.25em] text-[#E5A855]">Marketplace</p>
            <h3 className="font-serif-d text-4xl lg:text-5xl mt-3">ACA / Obamacare</h3>
            <p className="mt-4 max-w-md text-[#F4F1EA]/85 leading-relaxed">See if you qualify for premium tax credits that can lower your monthly cost — sometimes dramatically. I handle the application, the documents and the renewals.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["Subsidy check", "Bronze → Platinum", "Special enrollment"].map((t) => <span key={t} className="text-xs rounded-full border border-white/30 px-3 py-1.5">{t}</span>)}
            </div>
            <Quote id="aca" light />
          </PhotoCard>
        </Card>

        <Card testid="bento-private" delay={0.08} className="lg:col-span-5 bg-[#F4F1EA] border border-[#0B281E]/10 p-7 lg:p-9 flex flex-col">
          <Building2 className="w-8 h-8 text-[#C88A35]" />
          <h3 className="font-serif-d text-3xl text-[#0B281E] mt-auto">Private Health Plans</h3>
          <p className="mt-3 text-[#52635B] leading-relaxed">Off-exchange and private-sector options for the self-employed, early retirees and anyone who wants broader networks.</p>
          <Quote id="private" />
        </Card>

        <Card testid="bento-dvh" delay={0.12} className="lg:col-span-5 min-h-[290px]">
          <PhotoCard img="/img/dental.jpg" alt="Patient at an eye exam">
            <div className="flex gap-3 mb-auto">{[Smile, Eye, Ear].map((I, i) => <span key={i} className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 grid place-items-center"><I className="w-5 h-5 text-[#E5A855]" /></span>)}</div>
            <h3 className="font-serif-d text-3xl">Dental, Vision &amp; Hearing</h3>
            <p className="mt-2 text-[#F4F1EA]/85 text-sm leading-relaxed">Cleanings, glasses, hearing aids — the care most health plans leave out.</p>
          </PhotoCard>
        </Card>

        <LifeCard Card={Card} />

        <Card testid="bento-college" delay={0.08} className="lg:col-span-7 min-h-[290px]">
          <PhotoCard img="/img/college.jpg" alt="Graduate celebrating with family">
            <GraduationCap className="w-8 h-8 text-[#E5A855] mb-auto" />
            <h3 className="font-serif-d text-3xl lg:text-4xl">College Savings</h3>
            <p className="mt-2 max-w-md text-[#F4F1EA]/85 text-sm leading-relaxed">Start early, save steadily. Strategies to help fund your child's education without derailing your own goals.</p>
          </PhotoCard>
        </Card>

        <Card testid="bento-retirement" delay={0.12} className="md:col-span-2 lg:col-span-7 bg-[#C88A35] p-7 lg:p-9 flex flex-col sm:flex-row sm:items-end gap-6">
          <div className="flex-1">
            <Sunrise className="w-8 h-8 text-[#071510]" />
            <h3 className="font-serif-d text-3xl lg:text-4xl text-[#071510] mt-6">Retirement Planning</h3>
            <p className="mt-2 text-[#071510]/80 leading-relaxed max-w-md">Turn savings into dependable income and protect what you've earned for the years ahead.</p>
          </div>
          <button onClick={() => openQuote("other")} data-testid="retirement-quote-btn" className="shrink-0 self-start sm:self-end rounded-full bg-[#071510] text-[#FAF8F5] px-6 py-3 text-sm font-semibold hover:bg-[#0B281E] transition-colors">Plan my retirement</button>
        </Card>
      </div>
    </div>
  </section>
);
